import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Paper,
  InputAdornment,
  Tooltip,
  Fab,
  Container,
  useTheme,
  TablePagination
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Category,
  DataObject,
  Assessment,
  Save,
  Cancel,
  Refresh,
  FilterList,
  CloudUpload,
  GetApp,
  CheckCircle,
  Error as ErrorIcon,
  CalendarToday,
  TableChart
} from '@mui/icons-material';
import axios from 'axios';

interface MasterDataItem {
  id: number;
  year: number;
  age_value: string;
  age_code: string;
  age_dr: number;
  nationality_value: string;
  nationality_code: string;
  nationality_dr: number;
  marital_value: string;
  marital_code: string;
  marital_dr: number;
  dependents_value: string;
  dependents_code: string;
  dependents_dr: number;
  education_value: string;
  education_code: string;
  education_dr: number;
  occupation_value: string;
  occupation_code: string;
  occupation_dr: number;
  job_type_value: string;
  job_type_code: string;
  job_type_dr: number;
  service_current_value: string;
  service_current_code: string;
  service_current_dr: number;
  service_uae_value: string;
  service_uae_code: string;
  service_uae_dr: number;
  company_listing_value: string;
  company_listing_code: string;
  company_listing_dr: number;
  monthly_salary_value: string;
  monthly_salary_code: string;
  monthly_salary_dr: number;
  post_money_dsr_value: string;
  post_money_dsr_code: string;
  post_money_dsr_dr: number;
}

const MasterDataInput: React.FC = () => {
  const theme = useTheme();
  const [masterData, setMasterData] = useState<MasterDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState<number | ''>('');

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog states
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear() + 1); // Default to next year to avoid conflicts
  const [importResult, setImportResult] = useState<{
    message: string;
    summary?: {
      records_imported: number;
      errors_count: number;
    };
    errors?: string[];
  } | null>(null);
  const [yearDataCheck, setYearDataCheck] = useState<{
    has_data: boolean;
    record_count: number;
  } | null>(null);
  const [checkingYear, setCheckingYear] = useState(false);
  const [deletingYear, setDeletingYear] = useState(false);

  // Template generation states
  const [generatingTemplate, setGeneratingTemplate] = useState(false);
  const [templateGenerated, setTemplateGenerated] = useState(false);
  const [templateFileName, setTemplateFileName] = useState<string>('');

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    // Reset to first page when year filter changes
    setPage(0);
    fetchMasterData();
  }, [yearFilter]);

  useEffect(() => {
    // Refetch data when pagination changes
    fetchMasterData();
  }, [page, rowsPerPage]);

  const fetchMasterData = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();

      if (yearFilter) {
        params.append('year', yearFilter.toString());
      }

      // Add pagination parameters
      params.append('page', (page + 1).toString()); // Backend uses 1-based pagination
      params.append('per_page', rowsPerPage.toString());

      const response = await axios.get(`/api/v1/master-data?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Handle new response format with pagination
      setMasterData(response.data.data || []);
      setTotalCount(response.data.pagination?.total_count || 0);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch master data');
      setLoading(false);
    }
  };

  // Template generation and download functions
  const handleGenerateTemplate = async () => {
    setGeneratingTemplate(true);
    setTemplateGenerated(false);
    setTemplateFileName('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/v1/master-data/generate-template', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.file_name) {
        setTemplateGenerated(true);
        setTemplateFileName(response.data.file_name);
        setError(null);
      } else {
        setError('Template generation failed - no file name returned');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate template');
      setTemplateGenerated(false);
    } finally {
      setGeneratingTemplate(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!templateGenerated || !templateFileName) {
      setError('Please generate the template first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v1/master-data/download-template/${templateFileName}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Master_Data_Input_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Reset template state after successful download
      setTemplateGenerated(false);
      setTemplateFileName('');
    } catch (err: any) {
      setError('Failed to download template');
    }
  };

  const handleExcelImport = async (file: File) => {
    setImporting(true);
    setImportResult(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('year', selectedYear.toString());

      const response = await axios.post('/api/v1/master-data/import-excel', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      fetchMasterData(); // Refresh the data
      checkYearData(selectedYear); // Refresh year data check
    } catch (err: any) {
      console.error('Import error:', err);
      const errorMessage = err.response?.data?.message || 'Import failed';
      const errorDetails = err.response?.data?.errors || [];

      setImportResult({
        message: errorMessage,
        summary: { records_imported: 0, errors_count: 1 },
        errors: errorDetails.length > 0 ? errorDetails : [errorMessage]
      });
    } finally {
      setImporting(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check if data already exists for the selected year
      if (yearDataCheck?.has_data) {
        alert(`Data already exists for year ${selectedYear} (${yearDataCheck.record_count} records). Please select a different year or delete existing data first.`);
        event.target.value = ''; // Reset the input
        return;
      }
      handleExcelImport(file);
    }
    // Reset the input
    event.target.value = '';
  };

  // Check if data exists for selected year
  const checkYearData = async (year: number) => {
    try {
      setCheckingYear(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/v1/master-data/check-year/${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setYearDataCheck(response.data);
    } catch (err: any) {
      console.error('Error checking year data:', err);
      setYearDataCheck(null);
    } finally {
      setCheckingYear(false);
    }
  };

  // Delete data for selected year
  const deleteYearData = async (year: number) => {
    if (!window.confirm(`Are you sure you want to delete all data for year ${year}? This will also delete related regression analysis and rating components data.`)) {
      return;
    }

    try {
      setDeletingYear(true);
      const token = localStorage.getItem('token');
      await axios.delete(`/api/v1/master-data/delete-year/${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh data and year check
      await fetchMasterData();
      await checkYearData(selectedYear);

      alert(`Successfully deleted all data for year ${year}`);
    } catch (err: any) {
      alert(`Error deleting data: ${err.response?.data?.message || 'Unknown error'}`);
    } finally {
      setDeletingYear(false);
    }
  };

  // Check data when selected year changes
  useEffect(() => {
    checkYearData(selectedYear);
  }, [selectedYear]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Calculate statistics
  const totalRecords = masterData.length;
  const uniqueYears = Array.from(new Set(masterData.map(item => item.year))).length;

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
              Master Data Input (Δ DR Values)
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage Δ DR values for credit risk assessment categories
            </Typography>
          </Box>
          <Tooltip title="Import master data">
            <Fab
              color="primary"
              size="medium"
              onClick={() => setImportDialogOpen(true)}
              sx={{
                boxShadow: theme.shadows[4],
                '&:hover': {
                  boxShadow: theme.shadows[8],
                }
              }}
            >
              <CloudUpload />
            </Fab>
          </Tooltip>
        </Box>

        {/* Year Selector */}
        <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <CalendarToday sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Select Year for Master Data Import
                  </Typography>
                  {checkingYear ? (
                    <CircularProgress size={20} />
                  ) : yearDataCheck?.has_data ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        label={`${yearDataCheck.record_count} records`}
                        color="success"
                        size="small"
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={deletingYear ? <CircularProgress size={16} /> : <Delete />}
                        onClick={() => deleteYearData(selectedYear)}
                        disabled={deletingYear}
                      >
                        {deletingYear ? 'Deleting...' : 'Delete Data'}
                      </Button>
                    </Box>
                  ) : (
                    <Chip label="No data" color="warning" size="small" />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose the year for which you want to import Δ DR values. Each year's data is stored separately.
                  {yearDataCheck?.has_data && " Data already exists for this year - you can delete it or choose a different year."}
                  {!yearDataCheck?.has_data && " No data exists for this year - import section is available below."}
                </Typography>
                <TextField
                  select
                  label="Import Year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  sx={{ minWidth: 200 }}
                  SelectProps={{
                    native: true,
                  }}
                >
                  {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </TextField>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Card sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s' }
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {totalRecords}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Records
                    </Typography>
                  </Box>
                  <TableChart sx={{ fontSize: 32, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Card sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s' }
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {uniqueYears}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Years Covered
                    </Typography>
                  </Box>
                  <CalendarToday sx={{ fontSize: 32, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search and Actions Bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              placeholder="Search records..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />

            <TextField
              placeholder="Filter by year..."
              variant="outlined"
              size="small"
              type="number"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value ? parseInt(e.target.value) : '')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 150 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh data">
              <IconButton onClick={fetchMasterData} sx={{ color: 'primary.main' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Filter options">
              <IconButton sx={{ color: 'primary.main' }}>
                <FilterList />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Excel Import/Export Section - Only show when no data exists for selected year */}
      {(!yearDataCheck?.has_data && !checkingYear) && (
        <Card sx={{ mb: 3, border: '2px dashed', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Excel Import/Export
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Import Δ DR values from Excel or download a template to get started.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Save />}
                onClick={handleGenerateTemplate}
                disabled={generatingTemplate}
                sx={{ minWidth: 180 }}
              >
                {generatingTemplate ? <CircularProgress size={20} /> : 'Generate Template'}
              </Button>

              <Button
                variant="outlined"
                startIcon={<GetApp />}
                onClick={handleDownloadTemplate}
                disabled={!templateGenerated || generatingTemplate}
                sx={{ minWidth: 180 }}
              >
                {templateGenerated ? 'Download Template' : 'Download Template (Generate First)'}
              </Button>

              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => setImportDialogOpen(true)}
                disabled={importing}
                sx={{ minWidth: 180 }}
              >
                {importing ? <CircularProgress size={20} /> : 'Import Excel'}
              </Button>

              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="excel-file-input"
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="excel-file-input">
                <Button
                  variant="text"
                  component="span"
                  sx={{ color: 'text.secondary' }}
                >
                  Select File
                </Button>
              </label>
            </Box>

            {/* Template Generation Status */}
            {templateGenerated && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success" sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Template generated successfully! Click "Download Template" to download the file.
                    </Typography>
                  </Box>
                </Alert>
              </Box>
            )}

            {/* Import Results */}
            {importResult && (
              <Box sx={{ mt: 3 }}>
                <Alert
                  severity={importResult.summary?.errors_count === 0 ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {importResult.summary?.errors_count === 0 ? (
                      <CheckCircle sx={{ fontSize: 20 }} />
                    ) : (
                      <ErrorIcon sx={{ fontSize: 20 }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {importResult.message}
                    </Typography>
                </Box>
              </Alert>

              {importResult.summary && (
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${importResult.summary.records_imported} records imported`}
                    color="primary"
                    size="small"
                  />
                  {importResult.summary.errors_count > 0 && (
                    <Chip
                      label={`${importResult.summary.errors_count} errors`}
                      color="error"
                      size="small"
                    />
                  )}
                </Box>
              )}

              {importResult.errors && importResult.errors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                    Errors encountered:
                  </Typography>
                  <Box sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                    {importResult.errors.map((error, index) => (
                      <Typography key={index} variant="body2" color="error" sx={{ mb: 0.5 }}>
                        • {error}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Master Data Table */}
      {masterData.length === 0 ? (
        <Card sx={{ p: 6, textAlign: 'center', backgroundColor: 'grey.50' }}>
          <TableChart sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No master data found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get started by importing your first master data Excel file
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setImportDialogOpen(true)}
            size="large"
          >
            Import Master Data
          </Button>
        </Card>
      ) : (
        <Card sx={{ mb: 3, width: '100%', overflow: 'hidden' }}>
          <CardContent sx={{ width: '100%', overflow: 'hidden' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Master Data Records
            </Typography>

            <Box sx={{ width: '100%', overflow: 'hidden' }}>
              <TableContainer
                component={Paper}
                sx={{
                  overflowX: 'auto',
                  overflowY: 'visible',
                  maxWidth: '100%',
                  width: '100%',
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.100',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'grey.500',
                    },
                  },
                }}
              >
              <Table size="small" sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 80 }}>Year</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>AGE</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>NATIONALITY</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>MARITAL STATUS</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>DEPENDENTS</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>EDUCATION</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>OCCUPATION</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>JOB TYPE</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>SERVICE CURRENT</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>SERVICE UAE</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>COMPANY LISTING</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>MONTHLY SALARY</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', minWidth: 120 }}>POST MONEY DSR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {masterData.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{item.year}</TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.age_value} ({item.age_code})<br />
                        <Chip label={`Δ DR: ${(item.age_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.nationality_value} ({item.nationality_code})<br />
                        <Chip label={`Δ DR: ${(item.nationality_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.marital_value} ({item.marital_code})<br />
                        <Chip label={`Δ DR: ${(item.marital_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.dependents_value} ({item.dependents_code})<br />
                        <Chip label={`Δ DR: ${(item.dependents_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.education_value} ({item.education_code})<br />
                        <Chip label={`Δ DR: ${(item.education_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.occupation_value} ({item.occupation_code})<br />
                        <Chip label={`Δ DR: ${(item.occupation_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.job_type_value} ({item.job_type_code})<br />
                        <Chip label={`Δ DR: ${(item.job_type_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.service_current_value} ({item.service_current_code})<br />
                        <Chip label={`Δ DR: ${(item.service_current_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.service_uae_value} ({item.service_uae_code})<br />
                        <Chip label={`Δ DR: ${(item.service_uae_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.company_listing_value} ({item.company_listing_code})<br />
                        <Chip label={`Δ DR: ${(item.company_listing_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.monthly_salary_value} ({item.monthly_salary_code})<br />
                        <Chip label={`Δ DR: ${(item.monthly_salary_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {item.post_money_dsr_value} ({item.post_money_dsr_code})<br />
                        <Chip label={`Δ DR: ${(item.post_money_dsr_dr * 100).toFixed(2)}%`} size="small" color="primary" sx={{ mt: 0.5 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </TableContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUpload />
          Import Master Data
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload your Excel file with Δ DR values. The file should follow the template format.
            Make sure to select the correct year for your data import.
          </Typography>

          {/* Year Selection - Prominent */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              ⚠️ Important: Select the correct year for import
            </Typography>
            <Typography variant="body2">
              Choose the year that corresponds to your Excel data. Each year's data is stored separately.
            </Typography>
          </Alert>

          <Box sx={{ mb: 3 }}>
            <TextField
              select
              label="Select Year for Import"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              fullWidth
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderWidth: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  }
                }
              }}
              SelectProps={{
                native: true,
              }}
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 2 + i).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </TextField>

            {/* Show data status for selected year */}
            {selectedYear && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {checkingYear ? (
                  <CircularProgress size={16} />
                ) : yearDataCheck?.has_data ? (
                  <>
                    <ErrorIcon color="error" fontSize="small" />
                    <Typography variant="body2" color="error.main">
                      Data already exists for {selectedYear} ({yearDataCheck.record_count} records).
                      Choose a different year or delete existing data first.
                    </Typography>
                  </>
                ) : (
                  <>
                    <CheckCircle color="success" fontSize="small" />
                    <Typography variant="body2" color="success.main">
                      Ready to import data for {selectedYear}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<GetApp />}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>

            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUpload />}
              disabled={importing}
            >
              {importing ? <CircularProgress size={20} /> : 'Select File'}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
              />
            </Button>
          </Box>

          {importing && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Importing data...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MasterDataInput;
